import React, { useState, useContext, useEffect, useMemo } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase/config'
import { ProductsContext } from '../context/products.context'
import LeftSidebar from './LeftSidebar'
import OptionsOutsideSelect from './optionsOutside'
import CipCard from './CipCard'
import { useCipModal } from '../hooks/useCipModal'
import CipModalDialog from './CipModalDialog'

// ----------------------
// Utility / Helper Functions
// ----------------------

// Summation of arrays of arrays. e.g. sumArrays([[1, 2], [3, 4]]) => [4, 6].
function sumArrays(arrOfArrs) {
  if (!arrOfArrs || arrOfArrs.length === 0) return [0, 0, 0, 0, 0, 0]
  return arrOfArrs.reduce((acc, cur) => {
    cur.forEach((val, idx) => {
      acc[idx] = (acc[idx] || 0) + val
    })
    return acc
  }, [])
}

// Ensure an array is exactly length 6 by truncation or zero-padding.
function normalizeArray(val) {
  if (!Array.isArray(val)) return [0, 0, 0, 0, 0, 0]
  const result = val.slice(0, 6)
  while (result.length < 6) {
    result.push(0)
  }
  return result
}

// Given enrollment data for a CIP, returns an aggregated [y1, y2, y3, y4, y5, yTotal].
function getAllCipEnrollment(cipEntries) {
  const allData = cipEntries
    .filter((item) => item.id === 'All')
    .map((z) => z.val)
  if (!allData || !allData.length) return [0, 0, 0, 0, 0, 0]
  return sumArrays(allData)
}

// ----------------------
// Heatmap Data Builder for One CIP
// ----------------------
// This function contains the same logic as your previous buildCombinedHeatMapData,
// but now it operates on a single CIP object (cipObj). It finds the overall enrollment
// via the allCip array, builds group counts, rows and a max-percent map.
function buildCombinedHeatMapForOneCip(cipObj, allCip, allArr, demoMap, raceGroups, specialGroups) {
  const cipAllObj = allCip.find((f) => f.cipcode === cipObj.cipcode)
  const cipAllEnrolled = cipAllObj ? cipAllObj.enrolled : [0, 0, 0, 0, 0, 0]

  // Build group->counts from all enrollment entries for this CIP.
  const flattened = cipObj.enrolled.flat()
  const groupMap = flattened.reduce((acc, { id, val }) => {
    if (id === "schoolName") return acc
    const arrVal = normalizeArray(val)
    if (!acc[id]) {
      acc[id] = arrVal
    } else {
      acc[id] = sumArrays([acc[id], arrVal])
    }
    return acc
  }, {})

  const buildRow = (group) => {
    const arr = groupMap[group] || [0, 0, 0, 0, 0, 0]
    return arr.map((value, idx) => {
      const denominator = cipAllEnrolled[idx] || 0
      const numerator = value || 0
      const cipPercent = denominator > 0 ? numerator / denominator : 0

      const demoArrForGroup = demoMap[group] || [0, 0, 0, 0, 0, 0]
      const demoArrAll = allArr
      const demoNumerator = demoArrForGroup[idx] || 0
      const demoDenominator = demoArrAll[idx] || 0
      const demoPercent = demoDenominator > 0 ? demoNumerator / demoDenominator : 0

      return {
        x: idx < 5 ? 2021 + idx : "total",
        denominator,
        numerator,
        cipPercent,
        demoNumerator,
        demoDenominator,
        demoPercent,
        // For your "value" color scale:
        y: denominator > 0 ? cipPercent - demoPercent : null,
      }
    })
  }

  // Build Race and Special rows.
  const raceData = raceGroups.map((group) => ({
    id: group,
    data: buildRow(group),
  }))
  const specialData = specialGroups.map((group) => ({
    id: group,
    data: buildRow(group),
  }))

  // Add spacer row if both group types exist.
  const spacerRow = {
    id: "spacer",
    data: Array.from({ length: 6 }, (_, idx) => ({
      x: idx < 5 ? 2021 + idx : "total",
      denominator: 0,
      numerator: 0,
      cipPercent: 0,
      demoNumerator: 0,
      demoDenominator: 0,
      demoPercent: 0,
      y: null,
    })),
  }

  let finalRows
  if (raceData.length > 0 && specialData.length > 0) {
    finalRows = [...raceData, spacerRow, ...specialData]
  } else {
    finalRows = raceData.length > 0 ? raceData : specialData
  }

  // Build a map of { [groupId]: maxPercent } for color scaling.
  const groupMaxPercents = {}
  for (const row of finalRows) {
    if (row.id === "spacer") continue
    const maxVal = row.data.reduce((acc, point) => {
      return Math.max(acc, point.cipPercent, point.demoPercent)
    }, 0)
    groupMaxPercents[row.id] = maxVal
  }

  return {
    rows: finalRows,
    groupMaxPercents,
  }
}

// ----------------------
// Main Component
// ----------------------
export const EquityAllHeatmap = () => {
  const {
    nontrad,
    selectedOption,
    schoolDemo,
    defaultDistrict,
    filterSession,
    selectedSchools,
  } = useContext(ProductsContext)

  const { isOpen, openModal, closeModal } = useCipModal()

  const [equityGrid, setEquityGrid] = useState([])

  // Convert selectedSchools array of objects => array of string values.
  const selectedSch = useMemo(
    () => selectedSchools.map((sch) => sch.value),
    [selectedSchools]
  )

  const allRaceEthnicityGroups = useMemo(
    () => [
      'American Indian/Alaskan Native',
      'Asian',
      'Black/African American',
      'Hispanic/Latino',
      'Native Hawaiian/Other Pacific Islander',
      'Two or More Races',
      'White',
      'Female',
      'Male',
      'X',
    ],
    []
  )

  const allSpecialPopulations = useMemo(
    () => [
      'Economically Disadvantaged',
      'English Learners',
      'Homeless',
      'Migrant',
      'Military Parent',
      'Section504',
      'Students w/ Disabilities',
    ],
    []
  )

  const [allArr, setAllArr] = useState([0, 0, 0, 0, 0, 0])
  const [demoMap, setDemoMap] = useState({})
  const labelAliases = {
    // Race/Ethnicity
    'American Indian/Alaskan Native': 'Am. Indian/AK Native',
    'Native Hawaiian/Other Pacific Islander': 'Hawaiian/Pac. Isl.',
    'Two or More Races': '2+ Races',
    'Black/African American': 'Black/Afr. Am.',
    // Special Populations
    'Economically Disadvantaged': 'Econ. Disadvantaged',
    'Students w/ Disabilities': 'Students w/ Disab.',
  }

  const aliasOrSelf = (value) => labelAliases[value] || value

  // ----------------------
  // Selected groups based on selectedOption.
  // ----------------------
  const equityFilterEthnicity = useMemo(() => {
    if (!selectedOption) return []
    return selectedOption
      .map((e) => (allRaceEthnicityGroups.includes(e.value) ? e.value : null))
      .filter(Boolean)
  }, [selectedOption, allRaceEthnicityGroups])

  const equityFilterSpecial = useMemo(() => {
    if (!selectedOption) return []
    return selectedOption
      .map((e) => (allSpecialPopulations.includes(e.value) ? e.value : null))
      .filter(Boolean)
  }, [selectedOption, allSpecialPopulations])

  // Calculate dynamic width for axis label offset.
  const dynamicWidth = useMemo(() => {
    const vals = selectedOption?.map((v) => aliasOrSelf(v.value)) || []
    const longest = vals.reduce((a, b) => (a.length < b.length ? b : a), '')
    return longest.length
  }, [selectedOption])

  // ----------------------
  // Fetch equityGrid from Firebase when defaultDistrict changes.
  // ----------------------
  useEffect(() => {
    async function initialize() {
      if (defaultDistrict?.value) {
        const docRef = doc(db, 'equityGrid_WA_2025', defaultDistrict.value)
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) {
          setEquityGrid(docSnap.data().equityGrid)
        }
      }
    }
    initialize()
  }, [defaultDistrict])

  // ----------------------
  // Build groupCipData: For each CIP, gather enrollment data across selected schools.
  // ----------------------
  const groupCipData = useMemo(() => {
    if (!equityGrid || equityGrid.length === 0) return []
    return equityGrid.map((item) => {
      const enrolled = item.enrollment
        .filter((d) => selectedSch.includes(d.schoolName))
        .map((d) => Object.entries(d).map(([key, val]) => ({ id: key, val })))
      const demo = schoolDemo
        ?.filter((d) => selectedSch.includes(d.schoolName))
        .map((d) => Object.entries(d).map(([key, val]) => ({ id: key, val })))
      return {
        cipcode: item.cipcode,
        enrolled,
        enrolledDemo: demo,
      }
    })
  }, [equityGrid, schoolDemo, selectedSch])

  // ----------------------
  // Build allCip: For each CIP, sum up the 'All' student counts across selected schools.
  // ----------------------
  const allCip = useMemo(() => {
    return groupCipData.map((e) => ({
      cipcode: e.cipcode,
      enrolled: getAllCipEnrollment(e.enrolled.flat()),
    }))
  }, [groupCipData])

  // ----------------------
  // Build heatmapByCip map:
  // For each CIP in groupCipData, calculate the heatmap rows and groupMaxPercents.
  // ----------------------
  const heatmapByCip = useMemo(() => {
    const map = {}
    groupCipData.forEach((cipObj) => {
      map[cipObj.cipcode] = buildCombinedHeatMapForOneCip(
        cipObj,
        allCip,
        allArr,
        demoMap,
        equityFilterEthnicity,
        equityFilterSpecial
      )
    })
    return map
  }, [groupCipData, allCip, allArr, demoMap, equityFilterEthnicity, equityFilterSpecial])

  // ----------------------
  // Filter CIP Cards.
  // ----------------------
  const filteredCips = useMemo(() => {
    if (!filterSession) return []

    let bySession;
    if (filterSession === "nontrad-female") {
      bySession = equityGrid.filter((item) => nontrad?.female.includes(item.cipcode))
    } else if (filterSession === "nontrad-male") {
      bySession = equityGrid.filter((item) => nontrad?.male.includes(item.cipcode))
    } else {
      bySession = equityGrid.filter(
        (item) =>
          item.clusterName === filterSession || item.programArea === filterSession
      )
    }

    return bySession.filter((item) => {
      const cipAll = allCip.find((a) => a.cipcode === item.cipcode)
      return cipAll && cipAll.enrolled.some((count) => count > 0)
    })
  }, [equityGrid, filterSession, allCip, nontrad])

  // Count non-traditional CIPs with enrollment data
  const nontradCounts = useMemo(() => {
    if (!equityGrid || equityGrid.length === 0 || !allCip) return { female: 0, male: 0 }

    const cipsWithEnrollment = new Set(
      equityGrid
        .filter((item) => {
          const cipAll = allCip.find((a) => a.cipcode === item.cipcode)
          return cipAll && cipAll.enrolled.some((count) => count > 0)
        })
        .map((item) => item.cipcode)
    )

    return {
      female: nontrad.female.filter((cip) => cipsWithEnrollment.has(cip)).length,
      male: nontrad.male.filter((cip) => cipsWithEnrollment.has(cip)).length,
    }
  }, [equityGrid, allCip, nontrad])

  // ----------------------
  // Set cell size and container dimensions.
  // ----------------------
  const cellSize = 40
  const colCount = 6

  const leftMargin = 50 + dynamicWidth * 4
  const topMargin = 21
  const rightMargin = 0
  const bottomMargin = 0

  const containerWidth = colCount * cellSize + leftMargin + rightMargin
  // The combined row count factors in a spacer row if both groups are present.
  const combinedRowCount = useMemo(() => {
    if (equityFilterEthnicity.length > 0 && equityFilterSpecial.length > 0) {
      return equityFilterEthnicity.length + equityFilterSpecial.length + 1
    } else {
      return Math.max(equityFilterEthnicity.length, equityFilterSpecial.length)
    }
  }, [equityFilterEthnicity, equityFilterSpecial])
  const containerHeight = combinedRowCount * cellSize + topMargin + bottomMargin

  // ----------------------
  // Aggregate demo data.
  // ----------------------
  useEffect(() => {
    if (!schoolDemo || selectedSch.length === 0) return
    const allData = schoolDemo
      .filter((d) => selectedSch.includes(d.schoolName))
      .map((d) => d['All'])
      .filter(Boolean)
    const newAllArr = sumArrays(allData)
    const finalAllArr = newAllArr.length === 6 ? newAllArr : [0, 0, 0, 0, 0, 0]
    const allGroups = [...equityFilterEthnicity, ...equityFilterSpecial]
    const tempDemoMap = {}
    allGroups.forEach((group) => {
      const groupData = schoolDemo
        .filter((d) => selectedSch.includes(d.schoolName))
        .map((d) => d[group] || null)
        .filter(Boolean)
      const summedGroup =
        groupData.length > 0 ? sumArrays(groupData) : [0, 0, 0, 0, 0, 0]
      tempDemoMap[group] =
        summedGroup.length === 6 ? summedGroup : [0, 0, 0, 0, 0, 0]
    })
    const isAllArrChanged =
      JSON.stringify(finalAllArr) !== JSON.stringify(allArr)
    const isDemoMapChanged =
      JSON.stringify(tempDemoMap) !== JSON.stringify(demoMap)
    if (isAllArrChanged) {
      setAllArr(finalAllArr)
    }
    if (isDemoMapChanged) {
      setDemoMap(tempDemoMap)
    }
  }, [
    schoolDemo,
    selectedSch,
    equityFilterEthnicity,
    equityFilterSpecial,
    allArr,
    demoMap,
  ])

  // ----------------------
  // Final Render: pass the pre-computed heatmap data to each CipCard.
  // ----------------------
  return (
    <div className="pl-[19.5rem] print:pl-0 pt-4 pb-24">
      <div className="mx-auto max-w-[1400px]">
        <LeftSidebar nontradFemaleCount={nontradCounts.female} nontradMaleCount={nontradCounts.male} />
        <div className="pl-11 pb-4 bg-gray-50">
          <h1 className="py-6 text-4xl font-bold text-slate-900">
            Equity by Student Group
          </h1>
          <OptionsOutsideSelect isMulti closeMenuOnSelect={false} />
        </div>
        <div className="pl-5 flex flex-wrap mx-auto pt-5 select-none bg-white">
          {filteredCips.map((item) => {
            const { rows, groupMaxPercents } =
              heatmapByCip[item.cipcode] || { rows: [], groupMaxPercents: {} }
            return (
              <CipCard
                key={item.cipcode}
                cipItem={item}
                nontrad={nontrad}
                rows={rows}
                groupMaxPercents={groupMaxPercents}
                // pass margins so CipCard uses the same layout:
                leftMargin={leftMargin}
                topMargin={topMargin}
                containerWidth={containerWidth}
                containerHeight={containerHeight}
                onHeaderClick={openModal}
              />
            )
          })}
        </div>
        <CipModalDialog isOpen={isOpen} onClose={closeModal} />
      </div>
    </div>
  )
}